/**
 * Live invalidation.
 *
 * The server announces *that* something changed; this module throws away the
 * stale render and asks React Router to re-run the current route's loaders. The
 * refetch goes through the normal authorized endpoints, so a screen can never
 * show something the stream itself was not allowed to send.
 *
 * EventSource is used rather than a WebSocket: it reconnects by itself, sends
 * the session cookie like any same-origin GET, and needs no second protocol.
 *
 * A STREAM BELONGS TO A SESSION, AND SESSIONS CHANGE UNDER IT
 *
 * The connection is opened once when the app boots and lives as long as the
 * document. Signing out and signing in as somebody else does not reload the
 * document, so a stream opened for the previous session was still attached -
 * and when the server noticed that session had been revoked, it announced
 * `session-ended` and this module signed out whoever was using the app by then.
 *
 * That is the bug behind "log in, get thrown out a minute later, log in again
 * and it sticks": the eviction belonged to a session the browser had already
 * replaced, and the redirect it caused reloaded the document, which is what made
 * the second attempt stable.
 *
 * So an eviction is now VERIFIED before it is obeyed. `/auth/me` is asked
 * whether the cookie the browser holds right now resolves to a live session: if
 * it does, the announcement was about a session that is gone and the answer is
 * to reconnect, not to sign anybody out. Only a cookie that no longer works ends
 * the session for the person.
 */
import type { DataRouter } from 'react-router';
import { clearCache, invalidateTable, setCacheLive, setRevalidate } from './cache.ts';
import { setOnWrite } from './api.ts';

export interface ChangeEvent {
  tbl: string;
  op: 'insert' | 'update' | 'delete';
  id: string | null;
  clinicId: string | null;
  userId: string | null;
}

/** Bursts are normal — a bulk edit is many rows — so coalesce before refetching. */
const DEBOUNCE_MS = 300;

/**
 * Reconnection policy.
 *
 * EventSource cannot see the HTTP status behind a failure, so a request the
 * server will *always* refuse looks exactly like a dropped connection. Retrying
 * a fixed 3s produced an endless 403 loop that filled the server log and taught
 * nobody anything. Backing off and then giving up means a genuine outage still
 * recovers, while a permanent refusal costs a handful of requests and stops.
 *
 * A successful connection resets both counters, so a long-lived session that
 * drops overnight reconnects with the full budget again.
 */
const RECONNECT_BASE_MS = 3_000;
const RECONNECT_MAX_MS = 60_000;
const MAX_CONSECUTIVE_FAILURES = 5;

/*
 * How long to wait before trying again after giving up.
 *
 * The fast backoff above tops out under two minutes, which is the right speed
 * for "the server just restarted" and the wrong speed for "a proxy in front of
 * this domain does not pass a long-lived response through" - retrying a
 * genuine refusal every few seconds would just fill the log. This used to STOP
 * FOREVER once it gave up: correct for a permanent refusal, and silently wrong
 * for anything that clears up later - a deploy, a restarted load balancer, an
 * intermittent proxy - which is exactly the shape of "live updates just never
 * came back" with no error anywhere to explain why. A slow, indefinite retry
 * costs one request every five minutes and recovers on its own either way.
 */
const GIVE_UP_RETRY_MS = 5 * 60_000;

/**
 * Tables whose change can alter what *this* user is allowed to see.
 *
 * Only these justify a full reload, because the session's resolved scope is
 * stale and re-running loaders under it would re-render the old permissions.
 *
 * `clinics` and `stores` are deliberately NOT here. A JMC operator creates and
 * edits those constantly; reloading the page on every such event threw away
 * whatever they were typing and looked exactly like the new record vanishing.
 * Those changes are ordinary data — a revalidation shows them.
 */
const SCOPE_TABLES = new Set([
  'clinic_memberships',
  'store_memberships',
  'support_elevations',
]);

export function connectRealtime(router: DataRouter): () => void {
  // Learned from the stream's own `ready` event, so the client never has to
  // guess who it is.
  let currentUserId: string | null = null;
  let source: EventSource | null = null;
  let debounce: ReturnType<typeof setTimeout> | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let closed = false;

  const revalidateSoon = () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => void router.revalidate(), DEBOUNCE_MS);
  };

  /*
   * A background refresh finishing is the second half of a change event: the
   * loaders already ran and handed the screen the copy they had, and this is
   * what puts the newer answer in front of the person. Same debounce, so a
   * change touching several cached screens still redraws once.
   */
  setRevalidate(revalidateSoon);

  /*
   * Our own writes force a revalidation directly, not only through the stream.
   *
   * This USED TO be `setOnWrite(clearCache)` alone - clearCache only helps the
   * NEXT loader run (a later navigation, or the debounced one below), so a
   * screen that saved and stayed put was showing its own pre-save answer until
   * either the SSE round trip landed or somebody reloaded. That round trip is
   * usually fast, but making the screen depend on a pub/sub channel to reflect
   * an action the same tab just took is backwards: this tab already knows the
   * write happened without needing to be told a second time by the server. If
   * the stream is ever slow, reconnecting, or has quietly given up (see
   * MAX_CONSECUTIVE_FAILURES below), the person who just clicked save was
   * exactly who this silently failed for.
   */
  setOnWrite(() => {
    clearCache();
    revalidateSoon();
  });

  function open() {
    if (closed) return;
    source = new EventSource('/api/v1/events', { withCredentials: true });

    // The server sends `ready` once the stream is actually established, which
    // is the only reliable signal that the connection was accepted.
    source.addEventListener('ready', (event) => {
      failures = 0;
      /*
       * Invalidation is being delivered again, so cached reads are safe again.
       * Starting from empty rather than from whatever survived the gap: the
       * stream has no replay, so anything cached before it dropped may have
       * been changed while nobody was listening.
       */
      clearCache();
      setCacheLive(true);
      const ready = JSON.parse((event as MessageEvent<string>).data) as { userId?: string };
      currentUserId = ready.userId ?? null;
    });

    source.addEventListener('change', (event) => {
      const change = JSON.parse((event as MessageEvent<string>).data) as ChangeEvent;

      // '*' is the resync the server sends after its listener reconnected:
      // notifications during the gap are gone, so assume everything is stale.
      // A revalidation covers it; a reload would discard in-progress edits for
      // something the loaders can refetch.
      if (change.tbl === '*') {
        clearCache();
        revalidateSoon();
        return;
      }

      // Reload only when the change names *this* user. Someone else's role
      // changing is not a reason to throw away the page they are working on.
      if (SCOPE_TABLES.has(change.tbl) && change.userId && change.userId === currentUserId) {
        clearCache();
        window.location.reload();
        return;
      }

      /*
       * Mark what this table fed, then re-run the loaders.
       *
       * The revalidation is unconditional and the cache is what makes it cheap.
       * A loader whose data is still current answers from memory with no
       * request at all; one whose data was just marked answers with the copy it
       * has and fetches behind it, so the screen never blanks and the newer
       * answer arrives a round trip later. Only a screen with nothing cached
       * waits.
       *
       * Deciding *which* loaders to re-run instead would mean teaching the
       * router the same table-to-screen map the cache already holds.
       */
      invalidateTable(change.tbl);
      revalidateSoon();
    });

    /*
     * The session this stream was opened with has ended.
     *
     * Three things can be true, and they need different answers:
     *
     *   the access is genuinely gone   - logout in another tab, suspension, a
     *                                    membership removed. Sign out.
     *   the browser has a newer cookie - somebody signed out and back in, as
     *                                    themselves or as somebody else, in this
     *                                    document. Reconnect.
     *   the server cannot say          - treat as gone.
     *
     * `/auth/me` distinguishes them with the credential the browser actually
     * holds, which is the only thing that can answer the question. It goes
     * through fetch rather than the api helper deliberately: a 401 here is
     * information, not an error to surface.
     */
    source.addEventListener('session-ended', () => {
      source?.close();
      void (async () => {
        const live = await fetch('/api/v1/auth/me', {
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        })
          .then((res) => res.ok)
          /*
           * The network answered nothing, which says nothing about the session.
           * Reconnecting is the safe half: if the session really is gone the
           * next attempt is refused, and the failure budget below ends the
           * stream quietly instead of throwing somebody out over one lost
           * request.
           */
          .catch(() => true);

        if (live) {
          // A stream from a session this browser has replaced. Start a new one
          // with the cookie it holds now, and keep the full failure budget.
          failures = 0;
          open();
          return;
        }

        closed = true;
        clearCache();
        setCacheLive(false);
        window.location.assign('/login');
      })();
    });

    source.onerror = () => {
      // Let the browser handle its own transient reconnects; only step in once
      // it has given up and closed the stream for good.
      if (source?.readyState !== EventSource.CLOSED || closed) return;

      failures += 1;
      if (failures > MAX_CONSECUTIVE_FAILURES) {
        /*
         * Give up quickly retrying, but not forever. Live updates stop and the
         * app still works - every screen loads its own data - but somebody's
         * own writes still show up immediately (see setOnWrite above), so
         * giving up here no longer means "this tab is now silently frozen"
         * even when it does mean "other people's changes will not appear
         * without a reload for a while".
         */
        console.warn('live updates unavailable; retrying periodically');
        // Nothing will invalidate from here until the retry below lands, so
        // nothing may be served stale in between.
        setCacheLive(false);
        retry = setTimeout(() => {
          failures = 0;
          open();
        }, GIVE_UP_RETRY_MS);
        return;
      }

      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (failures - 1), RECONNECT_MAX_MS);
      retry = setTimeout(open, delay);
    };
  }

  open();

  return () => {
    closed = true;
    setCacheLive(false);
    if (debounce) clearTimeout(debounce);
    if (retry) clearTimeout(retry);
    source?.close();
  };
}
